import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';

/** Reads `400`, `400px` or `` as a pixel count, ignoring `%`, `auto` and junk. */
function parseCssPixels(value: string | null | undefined): number | null {
	if (!value || !/^\d+(\.\d+)?(px)?$/.test(value.trim())) return null;
	return Math.round(parseFloat(value));
}

/**
 * Builds the inline image node used by {@link RichTextEditor}: drag to
 * reposition, corner handles to resize.
 *
 * `dataAttr` is the `data-*` attribute the uploaded image's id is persisted in.
 * Each consumer keeps its own image library, so each passes its own attribute
 * (CAM programs use `data-cam-image-id`) and content moved between editors
 * cannot resolve against the wrong library.
 */
export function createInlineImageExtension(dataAttr: string) {
	const InlineImageView: React.FC<NodeViewProps> = ({
		node,
		updateAttributes,
		selected,
	}) => {
		const containerRef = useRef<HTMLDivElement>(null);
		const [resizing, setResizing] = useState(false);
		const startState = useRef({ x: 0, width: 0 });

		const width = node.attrs.width as number | null;
		const src = node.attrs.src as string;
		const alt = (node.attrs.alt as string) || '';
		const title = (node.attrs.title as string) || undefined;
		const imageId = (node.attrs.imageId as string) || undefined;

		const onPointerDown = useCallback(
			(e: React.PointerEvent) => {
				e.preventDefault();
				e.stopPropagation();
				const img = containerRef.current?.querySelector('img');
				if (!img) return;
				startState.current = { x: e.clientX, width: img.offsetWidth };
				setResizing(true);

				const onMove = (ev: PointerEvent) => {
					const delta = ev.clientX - startState.current.x;
					const next = Math.max(60, startState.current.width + delta);
					updateAttributes({ width: Math.round(next) });
				};
				const onUp = () => {
					setResizing(false);
					document.removeEventListener('pointermove', onMove);
					document.removeEventListener('pointerup', onUp);
				};
				document.addEventListener('pointermove', onMove);
				document.addEventListener('pointerup', onUp);
			},
			[updateAttributes]
		);

		useEffect(() => {
			if (!resizing) return;
			document.body.style.userSelect = 'none';
			return () => {
				document.body.style.userSelect = '';
			};
		}, [resizing]);

		return (
			<NodeViewWrapper
				className={`rte-image${selected ? ' rte-image--selected' : ''}${
					resizing ? ' rte-image--resizing' : ''
				}`}
				data-drag-handle=""
			>
				<div ref={containerRef} className="rte-image__frame">
					<img
						src={src}
						alt={alt}
						title={title}
						{...{ [dataAttr]: imageId }}
						style={{
							width: width ? `${width}px` : undefined,
							display: 'block',
						}}
						draggable={false}
					/>
					{selected && (
						<>
							<div
								className="rte-image__handle nw"
								onPointerDown={onPointerDown}
							/>
							<div
								className="rte-image__handle ne"
								onPointerDown={onPointerDown}
							/>
							<div
								className="rte-image__handle sw"
								onPointerDown={onPointerDown}
							/>
							<div
								className="rte-image__handle se"
								onPointerDown={onPointerDown}
							/>
						</>
					)}
				</div>
			</NodeViewWrapper>
		);
	};

	return Node.create({
		name: 'image',
		group: 'block',
		draggable: true,
		selectable: true,

		addAttributes() {
			return {
				src: { default: null },
				alt: { default: null },
				title: { default: null },
				width: {
					default: null,
					// `renderHTML` below persists the size as CSS, so read it back
					// the same way — otherwise reopening a note for editing loses
					// every resize the author made.
					parseHTML: (element) =>
						parseCssPixels(element.style.width) ??
						parseCssPixels(element.getAttribute('width')),
				},
				imageId: {
					default: null,
					parseHTML: (element) => element.getAttribute(dataAttr),
					renderHTML: (attributes) =>
						attributes.imageId ? { [dataAttr]: attributes.imageId } : {},
				},
			};
		},

		parseHTML() {
			return [{ tag: 'img[src]' }];
		},

		renderHTML({ HTMLAttributes }) {
			const attrs = { ...HTMLAttributes };
			// Persist the resized width as CSS so the read-only viewer honors it
			// without needing the node view.
			if (attrs.width) {
				attrs.style = `width: ${attrs.width}px`;
				delete attrs.width;
			}
			return ['img', mergeAttributes(attrs)];
		},

		addNodeView() {
			return ReactNodeViewRenderer(InlineImageView);
		},
	});
}
